/**
 * Cấu hình điều hướng sidebar cho từng workspace.
 *
 * Mỗi route trỏ tới các trang ĐÃ CÓ trong app (tái dùng tính năng cũ) hoặc
 * tới các trang workspace mới. Mục `comingSoon` sẽ được xây ở Phase 1–3.
 *
 * Icon dùng tên component của lucide-react (PascalCase).
 */

import type { WorkspaceMode, WorkspaceNavSection, TeacherStage } from '../types';

/**
 * Suy ra cấp học của giáo viên từ chuỗi `activeGradeLevel` (hoặc tên lớp).
 * Bền vững với nhiều định dạng: "Mầm non", "Tiểu học", "THCS", "THPT",
 * hoặc số khối ("Lớp 3", "10A1"…). Trả null khi không chắc → sidebar hiện đủ.
 */
export function classifyTeacherStage(raw?: string | null): TeacherStage | null {
  const s = (raw || '')
    .toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '');
  if (!s) return null;
  if (s.includes('mam non') || s.includes('mau giao')) return 'mam-non';
  if (s.includes('tieu hoc')) return 'tieu-hoc';
  if (s.includes('thpt')) return 'thpt';
  if (s.includes('thcs')) return 'thcs';
  // Suy theo số khối lớp nếu có.
  const m = s.match(/\b(\d{1,2})\b/);
  if (m) {
    const g = parseInt(m[1], 10);
    if (g >= 1 && g <= 5) return 'tieu-hoc';
    if (g >= 6 && g <= 9) return 'thcs';
    if (g >= 10 && g <= 12) return 'thpt';
  }
  return null;
}

/** Route gốc của từng workspace. */
export const WORKSPACE_ROUTES: Record<WorkspaceMode, string> = {
  teacher: '/workspace/giao-vien',
  student: '/workspace/hoc-sinh',
  parent: '/workspace/phu-huynh',
};

/** Nhãn & phụ đề hiển thị dưới logo theo mode. */
export const WORKSPACE_BRAND: Record<WorkspaceMode, { subtitle: string }> = {
  teacher: { subtitle: 'Teacher Workspace' },
  student: { subtitle: 'Student Workspace' },
  parent: { subtitle: 'Parent Workspace' },
};

/**
 * Điều hướng giáo viên — tổ chức theo CÔNG VIỆC thực tế, không theo loại công cụ:
 *   (home) Soạn bài → HỌC LIỆU → LỚP HỌC → KIỂM TRA & ĐÁNH GIÁ → PHÂN TÍCH → CÔNG CỤ AI
 *
 * Nguyên tắc:
 *   - Học liệu GẮN 1 bài giảng (giáo án, slide, quiz, game…) → tạo trong canvas (node),
 *     KHÔNG đặt ở sidebar (tránh trùng lặp gây rối).
 *   - Sidebar chỉ chứa nghiệp vụ NGOÀI phạm vi 1 bài: kho lưu trữ, lớp học, đánh giá
 *     tổng hợp, phân tích, công cụ độc lập (SKKN, AI…).
 *   - "KIỂM TRA & ĐÁNH GIÁ" thích ứng theo cấp học (gradeStages): mầm non/tiểu học →
 *     nhận xét; THCS/THPT → ra đề thi + ngân hàng câu hỏi (ma trận đề).
 */
const TEACHER_NAV: WorkspaceNavSection[] = [
  {
    // Không có title → nhóm home, không có divider tiêu đề
    items: [
      { key: 'overview', label: 'Tổng quan', icon: 'LayoutDashboard', path: '/workspace/giao-vien/tong-quan' },
      { key: 'canvas', label: 'Soạn bài giảng', icon: 'LayoutGrid', path: '/workspace/giao-vien/bai-giang' },
    ],
  },
  {
    // Mọi học liệu GẮN VỚI BÀI GIẢNG (giáo án, slide, quiz, game, hoạt động…) được
    // tạo ngay trong canvas dưới dạng node — KHÔNG đặt ở sidebar nữa (tránh trùng lặp).
    // Section này chỉ chứa: kho lưu trữ + công cụ ĐỘC LẬP không thuộc bài giảng nào.
    title: 'HỌC LIỆU',
    items: [
      { key: 'lib-mine', label: 'Kho học liệu của tôi', icon: 'FolderOpen', path: '/thu-vien' },
      { key: 'lib-community', label: 'Thư viện cộng đồng', icon: 'Library', path: '/thu-vien-cong-dong' },
      // Sơ đồ tư duy: công cụ độc lập (mindmap + flashcard), không gắn 1 bài → để ở sidebar.
      { key: 'mindmap', label: 'Sơ đồ tư duy', icon: 'BrainCircuit', path: '/so-do-tu-duy' },
      // Kế hoạch năm học (PPCT): span cả năm học, không gắn 1 bài → để ngoài node canvas.
      { key: 'year-plan', label: 'Kế hoạch năm học (PPCT)', icon: 'CalendarRange', path: '/ke-hoach-nam' },
      // SKKN: sáng kiến của giáo viên — KHÔNG thuộc bài giảng → để ngoài luồng node.
      { key: 'create-skkn', label: 'Trợ lý viết SKKN', icon: 'FileText', path: '/tao-sang-kien-kinh-nghiem' },
      // Hồ sơ & Biên bản hành chính — tồn tại ngoài phạm vi 1 bài giảng.
      { key: 'professional-portfolio', label: 'Hồ sơ chuyên môn số', icon: 'Briefcase', path: '/ho-so-chuyen-mon' },
      { key: 'meeting-minutes', label: 'Biên bản họp tổ CM', icon: 'NotebookPen', path: '/bien-ban-hop-to' },
    ],
  },
  {
    title: 'LỚP HỌC',
    items: [
      {
        key: 'classroom',
        label: 'Lớp học & Học sinh',
        icon: 'Users',
        path: '/quan-ly-lop-hoc',
        children: [
          { key: 'class-manage', label: 'Quản lý lớp học', icon: 'Users', path: '/quan-ly-lop-hoc' },
          { key: 'class-gradebook', label: 'Sổ điểm điện tử', icon: 'Table2', path: '/workspace/giao-vien/so-diem' },
          { key: 'class-attendance', label: 'Điểm danh & Chuyên cần', icon: 'CalendarCheck', path: '/workspace/giao-vien/diem-danh' },
          { key: 'class-toolbox', label: 'Hộp công cụ lớp học', icon: 'Dices', path: '/hop-cong-cu-lop' },
        ],
      },
      { key: 'class-pets', label: 'Lớp học Thú cưng', icon: 'Smile', path: '/workspace/giao-vien/thu-cung' },
      { key: 'schedule', label: 'Lịch dạy', icon: 'CalendarDays', path: '/workspace/giao-vien/lich-day' },
      // Trường học (School Organization) — org-level, span nhiều lớp/GV, khác hẳn
      // 1 lớp cụ thể — trước đây route tồn tại nhưng KHÔNG có mục menu nào trỏ tới.
      { key: 'school-manage', label: 'Quản lý trường học', icon: 'Landmark', path: '/quan-ly-truong-hoc' },
    ],
  },
  {
    // Nghiệp vụ đánh giá — NGOÀI luồng 1 bài (scoped theo môn × lớp × kỳ).
    // Thích ứng theo cấp: mầm non/tiểu học → nhận xét; THCS/THPT → ra đề + ngân hàng.
    title: 'KIỂM TRA & ĐÁNH GIÁ',
    items: [
      { key: 'assess-exam', label: 'Ra đề thi / kiểm tra', icon: 'ClipboardList', path: '/tao-de-kiem-tra', gradeStages: ['tieu-hoc', 'thcs', 'thpt'] },
      { key: 'assess-bank', label: 'Ngân hàng câu hỏi', icon: 'FileQuestion', path: '/workspace/giao-vien/ngan-hang-cau-hoi', gradeStages: ['tieu-hoc', 'thcs', 'thpt'] },
      { key: 'assess-oral', label: 'Kiểm tra vấn đáp', icon: 'Mic', path: '/kiem-tra-kien-thuc/tao' },
      // Mầm non/tiểu học: đánh giá bằng nhận xét sự phát triển (chưa build — roadmap).
      { key: 'assess-narrative', label: 'Đánh giá nhận xét', icon: 'MessageSquare', path: '/workspace/giao-vien/danh-gia-nhan-xet', gradeStages: ['mam-non', 'tieu-hoc'], comingSoon: true },
    ],
  },
  {
    title: 'PHÂN TÍCH',
    items: [
      { key: 'insight-analytics', label: 'Phân tích lớp học', icon: 'BarChart3', path: '/workspace/giao-vien/phan-tich' },
      { key: 'insight-parent', label: 'Báo cáo phụ huynh', icon: 'Mail', path: '/workspace/giao-vien/bao-cao-phu-huynh' },
    ],
  },
  {
    title: 'CÔNG CỤ AI',
    items: [
      { key: 'ai-lab', label: 'Phòng Lab AI', icon: 'FlaskConical', path: '/phong-lab-ai' },
      { key: 'ai-ethics', label: 'Thử thách Đạo đức AI', icon: 'Scale', path: '/thu-thach-dao-duc-ai' },
      { key: 'ai-factcheck', label: 'Kiểm chứng thông tin AI', icon: 'ShieldCheck', path: '/kiem-chung-thong-tin-ai' },
    ],
  },
];

const STUDENT_NAV: WorkspaceNavSection[] = [
  {
    items: [
      { key: 'dashboard', label: 'Tổng quan', icon: 'LayoutDashboard', path: '/workspace/hoc-sinh' },
    ],
  },
  {
    title: 'HỌC TẬP',
    items: [
      { key: 'schedule', label: 'Lịch học của tôi',    icon: 'CalendarDays', path: '/workspace/hoc-sinh/lich-hoc' },
      { key: 'tutor',    label: 'Gia sư AI',            icon: 'Bot',          path: '/workspace/hoc-sinh/gia-su',  badge: 'AI' },
      { key: 'goals',    label: 'Bài tập & Mục tiêu',  icon: 'Target',       path: '/workspace/hoc-sinh/muc-tieu' },
      { key: 'practice', label: 'Tự luyện đề thi',     icon: 'ClipboardList', path: '/workspace/hoc-sinh/luyen-de' },
      { key: 'progress', label: 'Tiến độ học tập',     icon: 'TrendingUp',   path: '/workspace/hoc-sinh/tien-do'  },
    ],
  },
  {
    title: 'CÔNG CỤ',
    items: [
      { key: 'roadmap',  label: 'Lộ trình học tập',    icon: 'Route',      path: '/workspace/hoc-sinh/lo-trinh' },
      {
        key: 'career', label: 'Khám phá nghề nghiệp', icon: 'Compass', path: '/hanh-trinh-kham-pha',
        children: [
          { key: 'career-explore', label: 'Khám phá nghề', icon: 'Compass',    path: '/hanh-trinh-kham-pha' },
          { key: 'career-profile', label: 'Hồ sơ của tôi', icon: 'FolderOpen', path: '/hanh-trinh-kham-pha' },
        ],
      },
      { key: 'mindmap',  label: 'Sơ đồ tư duy',        icon: 'BrainCircuit', path: '/so-do-tu-duy'      },
      { key: 'library',  label: 'Thư viện học liệu',   icon: 'BookOpen',   path: '/thu-vien'             },
    ],
  },
];

const PARENT_NAV: WorkspaceNavSection[] = [
  {
    items: [
      { key: 'overview', label: 'Tổng quan', icon: 'ChartPie', path: '/workspace/phu-huynh' },
    ],
  },
  {
    title: 'THEO DÕI CON',
    items: [
      { key: 'assignments', label: 'Nhiệm vụ & Bài tập',  icon: 'ClipboardList', path: '/workspace/phu-huynh/bai-tap'    },
      { key: 'tracking',  label: 'Tiến độ học tập',     icon: 'LineChart',     path: '/workspace/phu-huynh/tien-do'   },
      { key: 'results',   label: 'Kết quả & Điểm số',   icon: 'Award',         path: '/workspace/phu-huynh/ket-qua'   },
      { key: 'activity',  label: 'Hoạt động & Dự báo',  icon: 'Activity',      path: '/workspace/phu-huynh/hoat-dong' },
      { key: 'alerts',    label: 'Cảnh báo & Nhắc nhở', icon: 'Bell',          path: '/workspace/phu-huynh/canh-bao'  },
    ],
  },
  {
    title: 'KẾ HOẠCH',
    items: [
      { key: 'learning-path', label: 'Lộ trình học tập', icon: 'Route',       path: '/workspace/phu-huynh/lo-trinh' },
      { key: 'plan',    label: 'Kế hoạch học tập', icon: 'CalendarCheck', path: '/workspace/phu-huynh/ke-hoach' },
      {
        key: 'career',  label: 'Hướng nghiệp',     icon: 'Compass',       path: '/workspace/phu-huynh/huong-nghiep',
        children: [
          { key: 'career-overview',  label: 'Tổng quan',           icon: 'Compass',    path: '/workspace/phu-huynh/huong-nghiep?tab=overview'  },
          { key: 'career-profiles',  label: 'Danh sách hồ sơ',     icon: 'FolderOpen', path: '/workspace/phu-huynh/huong-nghiep?tab=profiles'  },
          { key: 'career-create',    label: 'Tạo hồ sơ cho con',   icon: 'PencilLine', path: '/workspace/phu-huynh/huong-nghiep?tab=create'    },
        ],
      },
    ],
  },
  {
    title: 'QUẢN LÝ',
    items: [
      { key: 'children-mgmt', label: 'Quản lý con', icon: 'Users', path: '/workspace/phu-huynh/quan-ly-con' },
    ],
  },
  {
    title: 'BÁO CÁO & LIÊN LẠC',
    items: [
      { key: 'reports',  label: 'Báo cáo định kỳ', icon: 'FileText',      path: '/workspace/phu-huynh', comingSoon: true },
      { key: 'messages', label: 'Tin nhắn GV',      icon: 'MessageSquare', path: '/workspace/phu-huynh', comingSoon: true },
    ],
  },
];

export const WORKSPACE_NAV: Record<WorkspaceMode, WorkspaceNavSection[]> = {
  teacher: TEACHER_NAV,
  student: STUDENT_NAV,
  parent: PARENT_NAV,
};
